import { Link } from "react-router-dom";
import { useConsent } from "@/context/ConsentContext";
import { useLang } from "@/context/LangContext";
import { useDict } from "@/i18n/dict";
import { GA_ENABLED } from "@/lib/analytics";

/**
 * RGPD-compliant cookie consent banner.
 * - Only renders when GA is configured AND no choice has been made yet.
 * - Three actions: accept all (analytics on), refuse all (analytics off), customise (link to legal page).
 * - Stored choice lives 6 months in localStorage (see ConsentContext).
 */
export default function ConsentBanner() {
  const { status, setStatus } = useConsent();
  const { lang } = useLang();
  const t = useDict(lang);

  // Don't render if GA not configured (no analytics to consent to)
  // or user has already chosen.
  if (!GA_ENABLED || status !== "pending") return null;

  return (
    <div
      data-testid="consent-banner"
      role="dialog"
      aria-live="polite"
      aria-label={t.consent.aria}
      className="fixed bottom-0 inset-x-0 z-[80] border-t border-[color:rgba(0,16,48,0.12)] bg-[color:var(--bg)]/95 backdrop-blur-md"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-5 lg:py-6 flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-8">
        <div className="flex-1 min-w-0">
          <p className="eyebrow mb-2">{t.consent.eyebrow}</p>
          <p className="text-sm leading-relaxed text-[color:var(--ink-2)] max-w-[60ch]">
            {t.consent.intro}{" "}
            <Link
              to="/legal/confidentialite"
              className="underline hover:text-[color:var(--ink)]"
              data-testid="consent-policy-link"
            >
              {t.consent.learnMore}
            </Link>
            .
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 lg:flex-shrink-0">
          <button
            data-testid="consent-refuse"
            onClick={() => setStatus("denied")}
            className="px-5 py-2.5 text-xs tracking-[0.22em] uppercase font-medium border border-[color:rgba(0,16,48,0.25)] hover:bg-[color:var(--bg-2)] transition-colors"
          >
            {t.consent.refuse}
          </button>
          <button
            data-testid="consent-accept"
            onClick={() => setStatus("granted")}
            className="px-5 py-2.5 text-xs tracking-[0.22em] uppercase font-medium bg-[color:var(--ink)] text-[color:var(--bg)] hover:opacity-90 transition-opacity"
          >
            {t.consent.accept}
          </button>
        </div>
      </div>
    </div>
  );
}
